import React, { useState } from 'react';
import { Bell, CheckCircle, AlertCircle, User, Key } from 'lucide-react';
import authServiceAdmin from '../authServiceAdmin';
import { useAdmin } from '../context/AdminContext';
import { getPushPermission, requestPushPermission } from '../utils/pushNotifications';

export default function SettingsPage() {
  const { currentAdmin, setCurrentAdmin } = useAdmin();
  const [profileData, setProfileData] = useState({ name: currentAdmin?.name || '', email: currentAdmin?.email || '' });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [pushPermission, setPushPermission] = useState(getPushPermission());
  const enablePush = async () => setPushPermission(await requestPushPermission());
  const pushLabel = { granted: 'Activées ✓', denied: 'Bloquées par le navigateur', default: 'Non activées', unsupported: 'Non prises en charge' }[pushPermission] || 'Non activées';

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    const result = await authServiceAdmin.updateProfile({ name: profileData.name });
    setSavingProfile(false);
    if (result?.success) {
      setCurrentAdmin(result.user);
      setMessage({ type: 'success', text: 'Profil mis à jour avec succès' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } else {
      setMessage({ type: 'error', text: result?.error || 'Erreur lors de la mise à jour' });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) { setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' }); return; }
    if (passwordData.newPassword.length < 8) { setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 8 caractères' }); return; }
    setSavingPassword(true);
    const result = await authServiceAdmin.changePassword(passwordData.currentPassword, passwordData.newPassword);
    setSavingPassword(false);
    if (result?.success) {
      setMessage({ type: 'success', text: 'Mot de passe modifié. Un e-mail de confirmation vous a été envoyé ; les autres appareils redemanderont un code de connexion.' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setMessage({ type: '', text: '' }), 6000);
    } else {
      setMessage({ type: 'error', text: result?.error || 'Erreur lors du changement de mot de passe' });
    }
  };

  return (
    <div className="dashboard-content">
      <h1 className="page-title">Paramètres</h1>
      {message.text && (
        <div className={`message-banner ${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {message.text}
        </div>
      )}
      <div className="settings-sections">
        <div className="settings-card">
          <div className="settings-header"><User size={24} /><h3>Profil Administrateur</h3></div>
          <div className="settings-content">
            <form onSubmit={handleProfileUpdate}>
              <div className="form-group">
                <label>Nom complet</label>
                <input type="text" value={profileData.name} onChange={(e) => setProfileData({...profileData, name: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={profileData.email} readOnly disabled />
                <small style={{ color: '#6B7280' }}>L'e-mail sert à la connexion et au code de sécurité : seul un superadmin peut le modifier.</small>
              </div>
              <button type="submit" className="btn-primary" disabled={savingProfile}>{savingProfile ? 'Enregistrement…' : 'Sauvegarder les modifications'}</button>
            </form>
          </div>
        </div>
        <div className="settings-card">
          <div className="settings-header"><Key size={24} /><h3>Sécurité</h3></div>
          <div className="settings-content">
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label>Mot de passe actuel</label>
                <input type="password" value={passwordData.currentPassword} onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})} placeholder="••••••••" required />
              </div>
              <div className="form-group">
                <label>Nouveau mot de passe</label>
                <input type="password" value={passwordData.newPassword} onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})} placeholder="••••••••" required />
              </div>
              <div className="form-group">
                <label>Confirmer le nouveau mot de passe</label>
                <input type="password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})} placeholder="••••••••" required />
              </div>
              <button type="submit" className="btn-primary" disabled={savingPassword}>{savingPassword ? 'Modification…' : 'Changer le mot de passe'}</button>
            </form>
          </div>
        </div>
        <div className="settings-card">
          <div className="settings-header"><Bell size={24} /><h3>Notifications</h3></div>
          <div className="settings-content">
            <div className="toggle-item">
              <div>
                <h4>Notifications push (bureau)</h4>
                <p>État : {pushLabel}
                  {pushPermission === 'denied' && ' — réautorisez-les dans les réglages du site de votre navigateur.'}
                </p>
              </div>
              {pushPermission === 'default' && (
                <button className="btn-primary" onClick={enablePush}>Activer</button>
              )}
              {pushPermission === 'granted' && <CheckCircle size={22} color="#10B981" />}
            </div>
            <div className="toggle-item">
              <div><h4>Nouveaux chauffeurs</h4><p>Recevoir une notification quand un chauffeur s'inscrit</p></div>
              <label className="toggle"><input type="checkbox" defaultChecked /><span className="toggle-slider"></span></label>
            </div>
            <div className="toggle-item">
              <div><h4>Paiements</h4><p>Recevoir une notification pour chaque paiement</p></div>
              <label className="toggle"><input type="checkbox" defaultChecked /><span className="toggle-slider"></span></label>
            </div>
            <div className="toggle-item">
              <div><h4>Nouveaux abonnés</h4><p>Recevoir une notification pour les nouveaux clients</p></div>
              <label className="toggle"><input type="checkbox" /><span className="toggle-slider"></span></label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
